import { useState, useEffect, useCallback } from 'react'

/**
 * Hook para gerenciar permissões de notificação do navegador
 * @returns {Object} Estado e métodos para gerenciar permissões
 */
export const useNotificationPermission = () => {
  const [permission, setPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  )
  const isSupported = typeof window !== 'undefined' && 'Notification' in window

  /**
   * Solicita permissão para notificações
   */
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('⚠️ Notificações não são suportadas neste navegador')
      return false
    }

    if (permission === 'granted') {
      return true
    }

    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      return result === 'granted'
    } catch (error) {
      console.error('❌ Erro ao solicitar permissão para notificações:', error)
      return false
    }
  }, [isSupported, permission])

  /**
   * Verifica se as notificações estão habilitadas
   */
  const isEnabled = useCallback(() => {
    return isSupported && permission === 'granted'
  }, [isSupported, permission])

  /**
   * Mostra uma notificação de teste
   */
  const showTestNotification = useCallback(() => {
    if (!isEnabled()) {
      console.warn('⚠️ Notificações não estão habilitadas')
      return false
    }

    try {
      const notification = new Notification('🔔 BDS - Teste de Notificação', {
        body: 'As notificações estão funcionando corretamente!',
        icon: '/favicon.ico',
        tag: 'bds-test-notification'
      })

      setTimeout(() => {
        notification.close()
      }, 3000)

      return true
    } catch (error) {
      console.error('❌ Erro ao mostrar notificação de teste:', error)
      return false
    }
  }, [isEnabled])

  // Monitora mudanças na permissão
  useEffect(() => {
    if (!isSupported) return

    const handlePermissionChange = () => {
      setPermission(Notification.permission)
    }

    // Alguns navegadores suportam o evento de mudança de permissão
    if ('permissions' in navigator && 'query' in navigator.permissions) {
      navigator.permissions.query({ name: 'notifications' }).then(result => {
        result.addEventListener('change', handlePermissionChange)
        
        return () => {
          result.removeEventListener('change', handlePermissionChange)
        }
      }).catch(() => {
        // Ignora erros se o navegador não suportar
      })
    }
  }, [isSupported])

  return {
    permission,
    isSupported,
    isEnabled: isEnabled(),
    requestPermission,
    showTestNotification
  }
}

export default useNotificationPermission